/**
 * Who is using the app, and what they are allowed to do.
 *
 * Sign-in is Yahoo, because the league already lives on Yahoo and Firebase Auth supports it
 * as a first-class OAuth provider. That choice buys something beyond convenience: the same
 * consent returns a Yahoo access token, so the roster and transaction sync that is currently
 * a Chrome-driven scrape can eventually run against the Fantasy API with the GM's own
 * credentials. Nothing here depends on that yet, and the extra scope is NOT requested until
 * Yahoo approves the app for it — see FANTASY_SCOPE below for why asking early breaks sign-in.
 *
 * The offline single-file build has no Firebase and no network. Rather than show a dead
 * sign-in button there, it drops to a GM picker: same Session shape, same role logic, every
 * screen reachable. That is what keeps the preview an honest preview.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react';
import { allManagers, currentManagers, managerRole } from '../domain/identity.js';
import type { GmId, Role, Session } from './types.js';

/**
 * Yahoo sign-in needs a Firebase project at runtime. Vite inlines these at build time; the
 * single-file build is produced without them, which is exactly how the app knows to run in
 * demo mode. No secrets here — a Firebase web config is public by design, and Firestore rules
 * are what actually enforce access.
 */
const FIREBASE_CONFIG = {
  apiKey: import.meta.env.VITE_FB_API_KEY as string | undefined,
  authDomain: import.meta.env.VITE_FB_AUTH_DOMAIN as string | undefined,
  projectId: import.meta.env.VITE_FB_PROJECT_ID as string | undefined,
  appId: import.meta.env.VITE_FB_APP_ID as string | undefined,
};

export const LIVE_AUTH = Boolean(FIREBASE_CONFIG.apiKey && FIREBASE_CONFIG.projectId);

/**
 * Whether to ask Yahoo for the Fantasy Sports read scope at sign-in.
 *
 * OFF by default, and that default is the whole point. Yahoo grants OpenID Connect the moment an
 * app exists but puts `fspt-r` behind a review queue — and a request for a scope the app has not
 * been approved for is not downgraded, it is REJECTED. The popup dies on Yahoo's error page
 * before anybody types a password, so asking for a scope we cannot use yet costs us sign-in
 * itself.
 *
 * So: sign in with the scopes we are entitled to today, and turn this on with
 * `VITE_YAHOO_FANTASY_SCOPE=1` the day approval lands. Nothing else changes — the server already
 * handles a link request whose token cannot read the league (it records "no team reported"), so
 * the app degrades to admin-confirmed pairing and upgrades to Yahoo-proposed pairing on a
 * one-line env change and a redeploy.
 */
const FANTASY_SCOPE = import.meta.env.VITE_YAHOO_FANTASY_SCOPE === '1';

/**
 * The managers holding a team this season.
 *
 * A live view of the roster rather than a snapshot, because the roster can now be added to from
 * inside the app: a manager who joins has to appear in the sign-in picker, the FAAB ledger and
 * every other list without a redeploy. The app shell subscribes to the roster version, so a
 * change here repaints everything below it.
 */
export const GMS: GmId[] = new Proxy([] as GmId[], {
  get: (_t, prop) => Reflect.get(currentManagers().map((m) => m.id), prop),
  has: (_t, prop) => Reflect.has(currentManagers().map((m) => m.id), prop),
  ownKeys: () => Reflect.ownKeys(currentManagers().map((m) => m.id)),
  getOwnPropertyDescriptor: (_t, prop) =>
    Reflect.getOwnPropertyDescriptor(currentManagers().map((m) => m.id), prop),
}) as GmId[];

/**
 * Who is who. The roster decides — `src/data/gm-roster.json` for the people who shipped with the
 * build, and Settings → Manager history for anybody added since. Tim built and runs the app, so
 * he is admin: everything a commissioner can do, plus acting as another GM to fix something on
 * their behalf.
 */
export const roleFor = (gm: GmId): Role => managerRole(gm);

export const ROLE_LABEL: Record<Role, string> = {
  admin: 'Admin',
  commissioner: 'Commissioner',
  manager: 'Manager',
  viewer: 'Viewer',
};

/**
 * Signed-in people who hold no team.
 *
 * Read off the roster rather than listed here, so making somebody a viewer is one field in
 * `gm-roster.json` — or one row in the manager overlay, which an admin can edit without a deploy
 * — and never a code change. They are deliberately NOT in `GMS`: that list is the men with a
 * seat, and it feeds the draft order, the ledgers, the "acting as" select and every other place
 * where appearing means owning something.
 */
export const viewers = (): GmId[] =>
  allManagers().filter((m) => m.role === 'viewer').map((m) => m.id);

/** The same list, live, for the components that render it. */
export const VIEWERS: GmId[] = new Proxy([] as GmId[], {
  get: (_t, prop) => Reflect.get(viewers(), prop),
  has: (_t, prop) => Reflect.has(viewers(), prop),
  ownKeys: () => Reflect.ownKeys(viewers()),
  getOwnPropertyDescriptor: (_t, prop) => Reflect.getOwnPropertyDescriptor(viewers(), prop),
}) as GmId[];

/**
 * Yahoo account to GM.
 *
 * NOT a table anybody maintains. When a manager signs in, Yahoo is asked what the holder of that
 * token owns — `users;use_login=1/games;game_keys=nfl/teams` — and it answers with his actual
 * team in league 338028. The server matches that team to a manager and writes the link down with
 * its reasoning. Nobody types an email address; there is nothing to keep in sync.
 *
 * A link starts UNCONFIRMED even when the team name matches exactly, because the cost of being
 * wrong is handing somebody else's team over. The admin confirms. Until then the app treats the
 * account as signed in but unrecognised, which is the safe default and says so on screen.
 */
export interface AccountLink {
  gm: string | null;
  confirmed: boolean;
  teamName: string | null;
  teamKey: string | null;
  why: string;
  /** Turned away by an admin. The sign-in screen says so rather than "ask him to link it". */
  rejected?: boolean;
}

async function fetchLink(idToken: string): Promise<AccountLink | null> {
  const res = await fetch('/api/me', { headers: { Authorization: `Bearer ${idToken}` } });
  if (!res.ok) return null;
  return ((await res.json()) as { link: AccountLink | null }).link;
}

/**
 * The signed-in user, held at module scope so a screen can mint a fresh ID token without the
 * session context having to hand one down through every component between here and there.
 *
 * A token rather than the user: it expires in an hour, so it has to be asked for at the moment of
 * the call and never stored. Null in the standalone build, which has no Firebase at all — callers
 * treat that as "no server" rather than as an error.
 */
let currentUser: { getIdToken(): Promise<string> } | null = null;

export async function idToken(): Promise<string | null> {
  try {
    return currentUser ? await currentUser.getIdToken() : null;
  } catch {
    return null;
  }
}

interface SessionCtx {
  session: Session | null;
  /** True while Firebase is deciding whether there is an existing session. */
  loading: boolean;
  live: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  /** Demo mode only: become a GM without authenticating. */
  actAs: (gm: GmId) => void;
  /** Admin only: act for another GM's team without losing your own identity. */
  viewAs: (gm: GmId | null) => void;
}

const Ctx = createContext<SessionCtx | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(LIVE_AUTH);

  useEffect(() => {
    if (!LIVE_AUTH) return;
    let cancelled = false;
    // Loaded on demand so the offline build never pulls Firebase into the bundle.
    (async () => {
      const { initializeApp } = await import('firebase/app');
      const { getAuth, onAuthStateChanged } = await import('firebase/auth');
      const auth = getAuth(initializeApp(FIREBASE_CONFIG as Record<string, string>));
      /**
       * `loading` stays true until we know WHO the user is, not merely THAT there is one.
       *
       * Firebase answers the second question from a token in local storage, instantly. The first
       * needs `/api/me`, which is a Cloud Function and can be a cold start — seconds, occasionally.
       * Clearing `loading` on the Firebase callback opened that whole window with `session` still
       * null, and the app's rule for null is "show the sign-in screen". So an already-signed-in
       * manager hard-refreshing was asked to sign in again, and if he was quick enough to click,
       * the app loaded behind the Yahoo popup he no longer needed.
       *
       * Nobody loses anything to it, but it teaches people to click a button that does nothing,
       * and on draft night it would read as the app having logged everybody out.
       */
      onAuthStateChanged(auth, (user) => {
        if (cancelled) return;
        currentUser = user;
        if (!user) {
          setSession(null);
          setLoading(false);
          return;
        }
        void (async () => {
          try {
            const link = await fetchLink(await user.getIdToken());
            const gm = link?.confirmed && link.gm ? (link.gm as GmId) : null;
            if (cancelled) return;
            setSession(gm
              ? {
                gm, role: roleFor(gm), uid: user.uid,
                displayName: user.displayName ?? gm, email: user.email ?? undefined,
              }
              // Signed in, but not confirmed as a GM. The UI shows what Yahoo said about the
              // account and who has to confirm it, rather than pretending they are nobody.
              : {
                gm: '', role: 'manager', uid: user.uid,
                displayName: user.displayName ?? 'Unlinked account', email: user.email ?? undefined,
                link: link ?? undefined,
              });
          } finally {
            // In a `finally` so a failed lookup lands on the sign-in screen rather than leaving
            // the app on "Checking your session…" for good. A stuck spinner is worse than a
            // wrong screen: one of them has a button on it.
            if (!cancelled) setLoading(false);
          }
        })();
      });
    })().catch(() => setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const signIn = useCallback(async () => {
    if (!LIVE_AUTH) return;
    const { initializeApp, getApps } = await import('firebase/app');
    const { getAuth, OAuthProvider, signInWithPopup } = await import('firebase/auth');
    const app = getApps()[0] ?? initializeApp(FIREBASE_CONFIG as Record<string, string>);
    const provider = new OAuthProvider('yahoo.com');
    // Read access to the user's fantasy data — only once Yahoo has approved the app for it.
    // Asking early does not degrade, it fails the whole sign-in. See FANTASY_SCOPE above.
    if (FANTASY_SCOPE) provider.addScope('fspt-r');
    provider.setCustomParameters({ prompt: 'login', language: 'en' });
    const result = await signInWithPopup(getAuth(app), provider);

    /**
     * The Yahoo access token comes back exactly once, in the sign-in result. Firebase does not
     * store it and cannot refresh it, so it goes straight to the server, which is the only place
     * that can call Yahoo anyway (the Fantasy API sends no CORS headers). One round trip decides
     * who this person is for good.
     */
    const cred = OAuthProvider.credentialFromResult(result);
    const yahooAccessToken = cred?.accessToken;
    if (!yahooAccessToken) return;
    await fetch('/api/link', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await result.user.getIdToken()}`,
      },
      body: JSON.stringify({ yahooAccessToken }),
    }).catch(() => undefined);
  }, []);

  const signOutFn = useCallback(async () => {
    if (!LIVE_AUTH) return setSession(null);
    const { getApps } = await import('firebase/app');
    const { getAuth, signOut } = await import('firebase/auth');
    const app = getApps()[0];
    if (app) await signOut(getAuth(app));
    setSession(null);
  }, []);

  const actAs = useCallback((gm: GmId) => {
    setSession({ gm, role: roleFor(gm), displayName: gm });
  }, []);

  /**
   * Admins only. Two things happen at once, and the second one is the important one.
   *
   * The real identity stays on the session, so every write remains attributable — an admin who
   * fixes a manager's slate should leave fingerprints rather than look like the manager.
   *
   * And the ENFORCED role drops to the impersonated GM's. The point of "view as" is to see what
   * he sees; carrying admin powers into it defeats that, and worse, it means the admin can walk
   * into a screen — the FAAB ledger, the lottery, the settings form — while wearing somebody
   * else's name. `realRole` is kept so he can still switch back.
   */
  const viewAs = useCallback((gm: GmId | null) => {
    setSession((prev) => {
      if (!prev) return prev;
      const real = prev.realGm ?? prev.gm;
      if ((prev.realRole ?? roleFor(real)) !== 'admin') return prev;
      return gm && gm.toUpperCase() !== real.toUpperCase()
        ? { ...prev, gm, realGm: real, actingAs: gm, role: roleFor(gm), realRole: 'admin' as Role }
        : {
          ...prev, gm: real, realGm: undefined, actingAs: undefined,
          role: roleFor(real), realRole: undefined,
        };
    });
  }, []);

  const value = useMemo(
    () => ({ session, loading, live: LIVE_AUTH, signIn, signOut: signOutFn, actAs, viewAs }),
    [session, loading, signIn, signOutFn, actAs, viewAs],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession() {
  const c = useContext(Ctx);
  if (!c) throw new Error('useSession must be used inside <SessionProvider>');
  return c;
}
